import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { W_WorkOrderReport_TimeCardEntry_VM } from 'ab-api/web/workOrderReport/fetchAccountTimeCard/viewModel';
import type { W_WorkOrderReport_TimeCardRejectedTimeSheet_VM } from 'ab-api/web/workOrderReport/findRejectedEntriesForAccount/viewModel';

import type TimeCardEntryVM from 'ab-viewModels/timeSheet/timeCardEntry.viewModel';

import { abbreviateTimeZone, getOffset } from '@acceligentllc/shared/utils/time';
import * as TimeSheetEntryUtils from '@acceligentllc/shared/utils/timeSheetEntry';

import type { RootState } from 'af-reducers';

import LoadingIndicator from 'af-components/LoadingIndicator';

import styles from './styles.module.scss';
import TimeCard from '../../../FieldReportsList/Modals/TimeCardTroubleshootModal/TimeCard';

interface OwnProps {
	accountId: number;
	fetchAccountTimeCard: (accountId: number) => Promise<W_WorkOrderReport_TimeCardEntry_VM[]>;
	findRejectedTimeSheetForTimeCard: (accountId: number) => Promise<W_WorkOrderReport_TimeCardRejectedTimeSheet_VM[]>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const TimeCardBodyWrapper: React.FC<Props> = (props) => {
	const {
		accountId,
		fetchAccountTimeCard,
		findRejectedTimeSheetForTimeCard,
		companyData,
	} = props;
	const [isLoading, setIsLoading] = React.useState(false);
	const [entries, setEntries] = React.useState<TimeCardEntryVM[]>([]);
	const [rejectedEntries, setRejectedEntries] = React.useState<W_WorkOrderReport_TimeCardRejectedTimeSheet_VM[]>([]);

	const getAccountEntries = React.useCallback(async () => {
		return await Promise.all([
			fetchAccountTimeCard(accountId),
			findRejectedTimeSheetForTimeCard(accountId),
		]);
	}, [accountId, fetchAccountTimeCard, findRejectedTimeSheetForTimeCard]);

	const unassignedEntries = entries?.filter(
		(_tse) => { return _tse.workOrderId === null && _tse.endTime; }
	);

	const accountOverlaps = entries ? TimeSheetEntryUtils.findOverlaps(entries) ?? {} : null;

	const fetchAndSetData = React.useCallback(async () => {
		setIsLoading(true);
		const [fetchedEntries, fetchedRejectedEntries] = await getAccountEntries();
		setEntries(fetchedEntries);
		setRejectedEntries(fetchedRejectedEntries);
		setIsLoading(false);
	}, [getAccountEntries]);

	React.useEffect(() => {
		fetchAndSetData();
	}, [fetchAndSetData]);

	const browserOffset = (new Date()).getTimezoneOffset();
	const timeZoneOffset = companyData.timezone ? getOffset(companyData.timezone) : 0;

	const timeOffset = timeZoneOffset + browserOffset;

	if (isLoading) {
		return (
			<div className={styles['side-panel__loading']}>
				<LoadingIndicator color="orange" />
			</div>
		);
	}

	return (
		<div className={styles.panelBody}>
			<div className="time-sheet-list__time-zone">The company's designated time zone is used. ({abbreviateTimeZone(companyData.timezone, 'z (Z)')})</div>
			<TimeCard
				allAccountEntries={entries}
				hasUnassignedEntries={unassignedEntries.length}
				hasUnassignedEntriesForMessage={unassignedEntries.length}
				overlaps={accountOverlaps}
				rejectedTSForAccount={rejectedEntries}
				timeOffset={timeOffset}
				timeZoneInUse={companyData.timezone}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(TimeCardBodyWrapper);
